import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ShareCard } from "../components/ShareCard";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Modal, Avatar } from "../components/Layout";
import { fmtDate } from "../lib/dates";
import { STEP_STATUS, RESPONSE_KINDS, CHAT_PLATFORMS, POD_PURPOSES } from "../lib/bouquet";
import { ArrowLeft, MessageCircle, Share2, Pencil, UserPlus, Hand, Brain, Handshake, Heart, CalendarDays, Plus, Sprout, ClipboardCopy, Link2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const KIND_ICONS = { cheer: Hand, check_in: MessageCircle, experience: Brain, help: Handshake, here: Heart };

export default function PodDetail() {
  const { podId } = useParams();
  const { user } = useAuth();
  const [pod, setPod] = useState(null);
  const [feed, setFeed] = useState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [shareCard, setShareCard] = useState(null);
  const [gatherings, setGatherings] = useState([]);
  const [moments, setMoments] = useState([]);
  const [mySteps, setMySteps] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showGathering, setShowGathering] = useState(false);
  const [sharePreview, setSharePreview] = useState(null); // {title, text}
  const [editForm, setEditForm] = useState({ name: "", description: "", chat_platform: "", chat_link: "", purposes: [], shared_intention: "" });
  const [memberEmail, setMemberEmail] = useState("");
  const [gForm, setGForm] = useState({ title: "", purpose: "", date: "", time: "", location: "", link_step_id: "", link: "", image_path: "" });
  const [gUploading, setGUploading] = useState(false);
  const [gPreview, setGPreview] = useState("");

  const pickGatheringImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setGUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/uploads/image", fd);
      setGForm((prev) => ({ ...prev, image_path: res.data.path }));
      setGPreview(`${api.defaults.baseURL}/files/${res.data.path}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setGUploading(false);
    }
  };
  const [linkForm, setLinkForm] = useState({ title: "", url: "" });

  const load = () => {
    api.get(`/pods/${podId}`).then((r) => {
      setPod(r.data);
      setEditForm({ name: r.data.name, description: r.data.description || "", chat_platform: r.data.chat_platform || "", chat_link: r.data.chat_link || "", purposes: (r.data.purpose || "").split(" · ").filter(Boolean), shared_intention: r.data.shared_intention || "" });
    }).catch((e) => { if (e.response?.status === 403) setForbidden(true); });
    api.get(`/pods/${podId}/feed`).then((r) => setFeed(r.data)).catch(() => {});
    api.get(`/pods/${podId}/gatherings`).then((r) => setGatherings(r.data)).catch(() => {});
    api.get("/moments").then((r) => setMoments(r.data.filter((m) => m.pod_id === podId))).catch(() => {});
    api.get("/intentions/my").then((r) => {
      const steps = [];
      r.data.forEach((i) => (i.steps || []).forEach((s) => { if (s.status === "active") steps.push({ step_id: s.step_id, text: s.text, intention: i.text }); }));
      setMySteps(steps);
    }).catch(() => {});
  };
  useEffect(load, [podId]); // eslint-disable-line react-hooks/exhaustive-deps

  const respond = async (step, kind) => {
    await api.post(`/steps/${step.step_id}/respond`, { kind });
    load();
  };

  const rsvp = async (g, status) => {
    await api.post(`/gatherings/${g.event_id}/rsvp`, { status });
    toast.success("RSVP saved");
    load();
  };

  const savePod = async () => {
    try {
      await api.put(`/pods/${podId}`, { name: editForm.name, description: editForm.description, chat_platform: editForm.chat_platform, chat_link: editForm.chat_link, purpose: editForm.purposes.join(" · "), shared_intention: editForm.shared_intention });
      toast.success("Pod updated");
      setShowEdit(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update pod");
    }
  };

  const addMember = async () => {
    if (!memberEmail.trim()) return;
    try {
      const res = await api.post(`/pods/${podId}/members`, { email: memberEmail });
      toast.success(`${res.data.name} joined the pod`);
      setShowAddMember(false);
      setMemberEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add member");
    }
  };

  const createGathering = async () => {
    if (!gForm.title.trim() || !gForm.date) return toast.error("Title and date are needed");
    try {
      await api.post(`/pods/${podId}/gatherings`, { ...gForm, link_step_id: gForm.link_step_id || null });
      toast.success("Gathering scheduled");
      setShowGathering(false);
      setGForm({ title: "", purpose: "", date: "", time: "", location: "", link_step_id: "", link: "", image_path: "" });
      setGPreview("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create gathering");
    }
  };

  const addLink = async () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) return toast.error("Add a title and a link");
    try {
      await api.post(`/pods/${podId}/links`, linkForm);
      setLinkForm({ title: "", url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add link");
    }
  };

  const deleteLink = async (l) => {
    await api.delete(`/pods/${podId}/links/${l.link_id}`);
    load();
  };

  const doShare = async () => {    const payload = { title: sharePreview.title, text: sharePreview.text };
    if (navigator.share) {
      try { await navigator.share(payload); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(sharePreview.text);
      toast.success("Copied — paste it in your group chat");
    }
    setSharePreview(null);
  };

  if (forbidden) {
    return (
      <div className="bg-card border border-border rounded-xl p-10 text-center space-y-3" data-testid="pod-forbidden">
        <p className="font-display text-xl font-medium">This pod is private</p>
        <p className="text-sm text-muted-foreground">Only its members can see what's shared here.</p>
        <Link to="/pods" className="text-primary text-sm font-semibold hover:underline">Back to my pods</Link>
      </div>
    );
  }
  if (!pod) return <div className="animate-pulse text-muted-foreground text-sm">Loading pod…</div>;

  const podShareText = `${pod.name}${pod.description ? ` — ${pod.description}` : ""}\n${pod.members.length} people · shared from Bouquet`;

  const discussInChat = (s) => {
    setSelectedStep(null);
    setShareCard({
      eyebrow: pod.name,
      title: `"${s.text}"`,
      body: `${s.author.name.split(" ")[0]} is working on: ${s.intention_text}${s.target_date ? ` · target ${fmtDate(s.target_date)}` : ""}`,
      footer: "Shared on Bouquet",
    });
  };

  const recentShare = feed.find((s) => s.author.user_id !== user.user_id && s.shared_at
    && (Date.now() - new Date(s.shared_at).getTime()) < 7 * 24 * 3600 * 1000);
  const podInviteText = `You're invited to join "${pod.name}" — my pod on Bouquet, where we keep track of what we're growing and show up for each other's next steps.${pod.shared_intention ? `\nOur shared intention: ${pod.shared_intention}` : ""}\nJoin here: ${window.location.origin}/join/${pod.pod_id}`;

  return (
    <div data-testid="pod-detail" className="space-y-8">
      <Link to="/pods" className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-pods">
        <ArrowLeft className="h-3.5 w-3.5" /> My Pods
      </Link>

      <PageHeader eyebrow="Pod" title={pod.name} description={pod.description || "What brings us together"}>
        <div className="flex flex-wrap gap-2">
          <button data-testid="edit-pod-button" onClick={() => setShowEdit(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
          <button data-testid="add-member-button" onClick={() => setShowAddMember(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors">
            <UserPlus className="h-3.5 w-3.5" /> Add member
          </button>
          {pod.chat_link && (
            <a data-testid="open-chat-button" href={pod.chat_link} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity">
              <MessageCircle className="h-3.5 w-3.5" /> Open {pod.chat_platform || "Group Chat"}
            </a>
          )}
          <button data-testid="copy-invite-button" onClick={() => setSharePreview({ title: `Invite to ${pod.name}`, text: podInviteText })}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors">
            <ClipboardCopy className="h-3.5 w-3.5" /> Copy Invite
          </button>
          <button data-testid="share-chat-button" onClick={() => setSharePreview({ title: pod.name, text: podShareText })}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors">
            <Share2 className="h-3.5 w-3.5" /> Share to Group Chat
          </button>
        </div>
      </PageHeader>

      {(pod.purpose || pod.shared_intention) && (
        <div className="flex flex-wrap items-center gap-3" data-testid="pod-purpose-row">
          {pod.purpose && (
            <span className="px-3 py-1.5 rounded-full bg-sage-pale text-xs font-semibold" data-testid="pod-purpose-badge">{pod.purpose}</span>
          )}
          {pod.shared_intention && (
            <p className="text-sm text-muted-foreground italic" data-testid="pod-shared-intention">Our shared intention: {pod.shared_intention}</p>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap" data-testid="pod-members-row">        {pod.members.map((m) => (
          <Link key={m.user_id} to={`/members/${m.user_id}`} data-testid={`pod-member-${m.user_id}`}
            className="flex items-center gap-2 hover:bg-secondary rounded-full pr-3 transition-colors">
            <Avatar user={m} size="h-9 w-9" />
            <p className="text-sm font-medium">{m.name}{m.user_id === user.user_id ? " (you)" : ""}</p>
          </Link>
        ))}
      </div>

      <section className="space-y-4" data-testid="pod-feed-section">
        <h3 className="font-display text-xl font-medium flex items-center gap-2"><Sprout className="h-5 w-5 text-primary" /> Shared Next Steps</h3>
        {recentShare && (
          <div className="bg-sage-pale border border-border rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="new-share-banner">
            <p className="text-sm flex-1 min-w-[200px]">
              <span className="font-semibold">{recentShare.author.name.split(" ")[0]}</span> just shared a next step: "{recentShare.text}" — drop a comment in your group chat.
            </p>
            <button data-testid="new-share-copy" onClick={() => discussInChat(recentShare)}
              className="px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity shrink-0">Copy a comment</button>
          </div>
        )}
        {feed.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing shared yet. When someone shares a next step with this pod, it appears here — with their consent, always.</p>
        )}
        <div className="space-y-4">
          {feed.map((s) => {
            const myKinds = s.responses.filter((r) => r.user_id === user.user_id).map((r) => r.kind);
            return (
              <div key={s.step_id} onClick={() => setSelectedStep(s)}
                className="bg-card border border-border rounded-xl p-5 space-y-3 cursor-pointer hover:border-primary transition-colors" data-testid={`feed-step-${s.step_id}`}>
                <div className="flex items-center gap-3">
                  <Avatar user={s.author} size="h-9 w-9" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{s.author.name}</p>
                    <p className="text-xs text-muted-foreground truncate">Working on: {s.intention_text}</p>
                  </div>
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-secondary text-[11px] font-semibold shrink-0">{STEP_STATUS[s.status] || s.status}</span>
                </div>
                <p className="text-sm">Next step: <span className="font-medium">{s.text}</span>
                  {s.target_date && <span className="text-muted-foreground text-xs"> · by {fmtDate(s.target_date)}</span>}
                </p>
                {s.support_preferences.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {s.support_preferences.map((p) => (
                      <span key={p} className="px-2.5 py-1 rounded-full bg-accent/15 border border-accent/30 text-[11px] font-medium">{p}</span>
                    ))}
                  </div>
                )}
                {s.responses.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {s.responses.map((r, idx) => (
                      <span key={idx} className="px-2.5 py-1 rounded-full bg-secondary text-[11px] font-medium">
                        {r.name.split(" ")[0]} · {RESPONSE_KINDS.find((k) => k.kind === r.kind)?.label}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {RESPONSE_KINDS.map(({ kind, label }) => {
                    const Icon = KIND_ICONS[kind];
                    const mine = myKinds.includes(kind);
                    return (
                      <button key={kind} data-testid={`respond-${kind}-${s.step_id}`} onClick={(e) => { e.stopPropagation(); respond(s, kind); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${mine ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                        <Icon className="h-3 w-3" /> {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="space-y-4" data-testid="gatherings-section">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-medium flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Upcoming Gatherings</h3>
          <button data-testid="create-gathering-button" onClick={() => setShowGathering(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors">
            <Plus className="h-3.5 w-3.5" /> Plan one
          </button>
        </div>
        {gatherings.length === 0 && <p className="text-sm text-muted-foreground">No gatherings yet — plan a dinner, a call, a walk.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {gatherings.map((g) => (
            <div key={g.event_id} className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid={`gathering-card-${g.event_id}`}>
              {g.image_path && (
                <img src={`${api.defaults.baseURL}/files/${g.image_path}`} alt={g.title} className="w-full h-32 object-cover rounded-lg" data-testid={`gathering-image-${g.event_id}`} />
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm">{g.title}</p>
                  {g.purpose && <p className="text-xs text-muted-foreground mt-0.5">{g.purpose}</p>}
                </div>
                <p className="text-xs font-semibold text-primary shrink-0">{fmtDate(g.date)}{g.time ? ` · ${g.time}` : ""}</p>
              </div>
              {g.location && <p className="text-xs text-muted-foreground">{g.location}</p>}
              {g.link && <a href={g.link} target="_blank" rel="noreferrer" data-testid={`gathering-link-${g.event_id}`}
                className="text-xs font-semibold text-primary hover:underline block">Event link →</a>}
              {g.linked_step && <p className="text-xs text-muted-foreground">Linked to a next step: "{g.linked_step.text}"</p>}
              <div className="grid grid-cols-3 gap-2">
                {[["going", "Going"], ["maybe", "Maybe"], ["cant", "Can't make it"]].map(([key, label]) => (
                  <button key={key} data-testid={`rsvp-${key}-${g.event_id}`} onClick={() => rsvp(g, key)}
                    className={`px-2 py-2 rounded-lg text-[11px] font-semibold transition-colors ${g.my_rsvp === key ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <button data-testid={`share-gathering-${g.event_id}`}
                onClick={() => setSharePreview({ title: g.title, text: `You're invited: ${g.title}${g.purpose ? `\n${g.purpose}` : ""}\n${fmtDate(g.date)}${g.time ? ` · ${g.time}` : ""}${g.location ? ` · ${g.location}` : ""}\nRSVP in Bouquet` })}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
                <Share2 className="h-3 w-3" /> Share invitation
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3" data-testid="pod-links-section">
        <h3 className="font-display text-xl font-medium flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> Pod Links</h3>
        {(pod.links || []).length === 0 && <p className="text-sm text-muted-foreground">Books, accounts, tools — anything the pod recommends lives here.</p>}
        <div className="space-y-2">
          {(pod.links || []).map((l) => (
            <div key={l.link_id} className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`pod-link-${l.link_id}`}>
              <a data-testid={`pod-link-open-${l.link_id}`} href={l.url} target="_blank" rel="noreferrer"
                className="text-sm font-medium text-primary hover:underline flex-1 min-w-0 truncate">{l.title}</a>
              <span className="text-[11px] text-muted-foreground shrink-0">{(l.added_by_name || "").split(" ")[0]}</span>
              {(l.added_by === user.user_id || pod.created_by === user.user_id) && (
                <button data-testid={`pod-link-delete-${l.link_id}`} onClick={() => deleteLink(l)}
                  className="text-muted-foreground hover:text-destructive px-1 shrink-0">×</button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="pod-link-title-input" value={linkForm.title} onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
            placeholder="What is it?" className="flex-1 min-w-0 px-3.5 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <input data-testid="pod-link-url-input" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
            placeholder="https://…" className="flex-1 min-w-0 px-3.5 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <button data-testid="pod-link-add" onClick={addLink}
            className="px-3.5 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-secondary transition-colors shrink-0">Add</button>
        </div>
      </section>

      {moments.length > 0 && (
        <section className="space-y-3" data-testid="pod-moments-strip">
          <h3 className="font-display text-xl font-medium flex items-center gap-2"><Heart className="h-5 w-5 text-primary" /> Moments in this pod</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {moments.slice(0, 4).map((m) => (
              <Link key={m.moment_id} to="/moments" className="bg-card border border-border rounded-xl p-4 space-y-2 hover:border-primary transition-colors block" data-testid={`pod-moment-${m.moment_id}`}>
                {m.image_path && (
                  <img src={`${api.defaults.baseURL}/files/${m.image_path}`} alt={m.title} className="w-full h-28 object-cover rounded-lg" />
                )}
                <p className="text-sm font-semibold">{m.title}</p>
                {m.description && <p className="text-xs text-muted-foreground line-clamp-2">{m.description}</p>}
                <p className="text-xs text-muted-foreground">{m.author?.name} · {fmtDate(m.date)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <ShareCard card={shareCard} onDone={() => setShareCard(null)} />

      {selectedStep && (        <Modal onClose={() => setSelectedStep(null)} testid="step-detail-modal">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar user={selectedStep.author} size="h-10 w-10" />
              <div className="min-w-0">
                <p className="text-sm font-semibold">{selectedStep.author.name}</p>
                <p className="text-xs text-muted-foreground truncate">Working on: {selectedStep.intention_text}</p>
              </div>
              <span className="ml-auto px-2 py-0.5 rounded-full bg-secondary text-[11px] font-semibold shrink-0">{STEP_STATUS[selectedStep.status] || selectedStep.status}</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Next step</p>
              <p className="font-display text-xl font-medium" data-testid="step-detail-text">{selectedStep.text}</p>
              {selectedStep.target_date && <p className="text-xs text-muted-foreground mt-1">Target: {fmtDate(selectedStep.target_date)}</p>}
            </div>
            {selectedStep.support_preferences.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">How they want support</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedStep.support_preferences.map((p) => (
                    <span key={p} className="px-2.5 py-1 rounded-full bg-accent/15 border border-accent/30 text-[11px] font-medium">{p}</span>
                  ))}
                </div>
              </div>
            )}
            {(selectedStep.items || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">Their checklist</p>
                <div className="space-y-1.5" data-testid="step-detail-items">
                  {selectedStep.items.map((it) => (
                    <p key={it.item_id} className={`text-sm flex items-center gap-2 ${it.done ? "text-muted-foreground line-through" : ""}`}>
                      <span className={`h-3.5 w-3.5 rounded-full border shrink-0 ${it.done ? "bg-primary border-primary" : "border-border"}`} />
                      {it.title}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {selectedStep.responses.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedStep.responses.map((r, idx) => (
                  <span key={idx} className="px-2.5 py-1 rounded-full bg-secondary text-[11px] font-medium">
                    {r.name.split(" ")[0]} · {RESPONSE_KINDS.find((k) => k.kind === r.kind)?.label}
                  </span>
                ))}
              </div>
            )}
            <button data-testid="step-discuss-chat" onClick={() => discussInChat(selectedStep)}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">Discuss in group chat</button>
          </div>
        </Modal>
      )}

      {sharePreview && (
        <Modal onClose={() => setSharePreview(null)} testid="chat-preview-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Preview before sharing</h3>
            <p className="text-xs text-muted-foreground">This exact text goes to your device's share sheet or clipboard. Nothing private is ever included.</p>
            <pre className="whitespace-pre-wrap bg-secondary rounded-lg p-4 text-sm font-sans" data-testid="share-preview-text">{sharePreview.text}</pre>
            <div className="flex gap-2">
              <button data-testid="share-confirm-button" onClick={doShare}
                className="flex-1 bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Share…</button>
              <button data-testid="share-copy-button"
                onClick={async () => { await navigator.clipboard.writeText(sharePreview.text); toast.success("Copied"); setSharePreview(null); }}
                className="flex-1 border border-border py-3 rounded-full text-sm font-semibold hover:bg-secondary transition-colors">Copy text</button>
            </div>
          </div>
        </Modal>
      )}

      {showEdit && (
        <Modal onClose={() => setShowEdit(false)} testid="edit-pod-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Edit pod</h3>
            <input data-testid="edit-pod-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputCls} />
            <textarea data-testid="edit-pod-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              rows={2} placeholder="What brings us together?" className={inputCls} />
            <div className="space-y-2">
              <p className="text-sm font-semibold">Pod purpose <span className="font-normal text-muted-foreground">(optional — pick any that fit; revisit anytime as the group evolves)</span></p>
              <div className="flex flex-wrap gap-2">
                {POD_PURPOSES.map((p) => (
                  <button key={p.key} type="button" data-testid={`edit-purpose-${p.key}`}
                    onClick={() => setEditForm((f) => {
                      const has = f.purposes.includes(p.label);
                      return { ...f, purposes: has ? f.purposes.filter((x) => x !== p.label) : [...f.purposes, p.label], shared_intention: has || f.shared_intention ? f.shared_intention : p.suggestion };
                    })}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${editForm.purposes.includes(p.label) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5">Our shared intention <span className="font-normal text-muted-foreground">(optional)</span></label>
              <textarea data-testid="edit-pod-intention" value={editForm.shared_intention}
                onChange={(e) => setEditForm({ ...editForm, shared_intention: e.target.value })}
                rows={2} placeholder="What this pod is for, in your own words…" className={inputCls} />
              {(() => {
                const ideas = [...new Set(POD_PURPOSES.filter((p) => editForm.purposes.includes(p.label)).flatMap((p) => p.gatherings))];
                return ideas.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1.5" data-testid="edit-gathering-ideas">
                    Gathering ideas if helpful: {ideas.join(" · ")}
                  </p>
                );
              })()}
            </div>
            <select data-testid="edit-pod-chat-platform" value={editForm.chat_platform} onChange={(e) => setEditForm({ ...editForm, chat_platform: e.target.value })} className={inputCls}>
              <option value="">Chat platform…</option>
              {CHAT_PLATFORMS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="edit-pod-chat-link" value={editForm.chat_link} onChange={(e) => setEditForm({ ...editForm, chat_link: e.target.value })}
              placeholder="Group chat link (https://…)" className={inputCls} />
            <button data-testid="edit-pod-save" onClick={savePod} className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Save</button>
          </div>
        </Modal>
      )}

      {showAddMember && (
        <Modal onClose={() => setShowAddMember(false)} testid="add-member-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Add a member</h3>
            <p className="text-xs text-muted-foreground">They must already be a Bouquet member. Trust first — add people you know.</p>
            <input data-testid="add-member-email" type="email" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} placeholder="their@email.com" className={inputCls} />
            <button data-testid="add-member-submit" onClick={addMember} className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Add to pod</button>
          </div>
        </Modal>
      )}

      {showGathering && (
        <Modal onClose={() => setShowGathering(false)} testid="create-gathering-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Plan a gathering</h3>
            <input data-testid="gathering-title-input" value={gForm.title} onChange={(e) => setGForm({ ...gForm, title: e.target.value })} placeholder="Title — e.g. Sunday check-in call" className={inputCls} />
            <textarea data-testid="gathering-purpose-input" value={gForm.purpose} onChange={(e) => setGForm({ ...gForm, purpose: e.target.value })} rows={2} placeholder="Purpose (optional)" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="gathering-date-input" type="date" value={gForm.date} onChange={(e) => setGForm({ ...gForm, date: e.target.value })} className={inputCls} />
              <input data-testid="gathering-time-input" value={gForm.time} onChange={(e) => setGForm({ ...gForm, time: e.target.value })} placeholder="7:00 PM" className={inputCls} />
            </div>
            <input data-testid="gathering-location-input" value={gForm.location} onChange={(e) => setGForm({ ...gForm, location: e.target.value })} placeholder="Location or meeting link" className={inputCls} />
            <input data-testid="pod-gathering-link-input" value={gForm.link} onChange={(e) => setGForm({ ...gForm, link: e.target.value })} placeholder="Link (optional — RSVP page, Zoom, menu…)" className={inputCls} />
            <div>
              <input type="file" accept="image/*" className="hidden" id="pod-gathering-image-upload" data-testid="pod-gathering-image-input" onChange={pickGatheringImage} />
              {gPreview ? (
                <img src={gPreview} alt="Gathering" className="w-full h-32 object-cover rounded-lg" data-testid="pod-gathering-image-preview" />
              ) : (
                <label htmlFor="pod-gathering-image-upload" data-testid="pod-gathering-image-label"
                  className="block w-full border border-dashed border-border rounded-lg py-5 text-center text-xs text-muted-foreground hover:bg-secondary transition-colors cursor-pointer">
                  {gUploading ? "Uploading…" : "Add a picture (optional)"}
                </label>
              )}
            </div>
            <select data-testid="gathering-link-step" value={gForm.link_step_id} onChange={(e) => setGForm({ ...gForm, link_step_id: e.target.value })} className={inputCls}>
              <option value="">Link to one of my next steps (optional)…</option>
              {mySteps.map((s) => <option key={s.step_id} value={s.step_id}>{s.text}</option>)}
            </select>
            <button data-testid="gathering-submit" onClick={createGathering} className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Schedule</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
